import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { observer } from 'mobx-react-lite';
import { boolean } from 'yup';
import {
  FormValues,
  IDataInputNames,
} from '../../widget-calculator-form.component';
import InputMessage from '../../../../../common/input-message/input-message.component';
import { useStore } from '../../../../../stores/store';
import { getMeasureUnits } from '../../../../../utils/parcel-measurements.ts/parcel-measurements-utils';

const ValidationHandler = ({
  activeField,
}: {
  setShowEstimatedDeliveryCost: (show: boolean) => void;
  handleDataInputClose: (inputName?: keyof FormValues) => void;
  activeField: IDataInputNames | null;
}) => {
  const { t } = useTranslation();
  const {
    widgetCalculatorStore: { deliveryLimitations },
    userStore: { measures },
  } = useStore();
  const { validateField, touched, errors, values, isValid } =
    useFormikContext<FormValues>();

  const measureUnits = getMeasureUnits(measures);

  useEffect(() => {
    Object.keys(values).forEach((fieldName) => {
      // Ignore validation while dropdown is still opened.
      const ignoreActiveRouteField =
        activeField === 'destination_search_string' ||
        activeField === 'departure_search_string';

      if (!ignoreActiveRouteField && touched[fieldName as keyof FormValues]) {
        validateField(fieldName);
      }
    });
  }, [touched, activeField, validateField, values]);

  const volumetricMessageIsShown =
    Object.keys(errors).includes('volumetricWeight');

  // Do not show other errors if volumetric error or warning are present
  if (volumetricMessageIsShown) return;

  const predictionsNotFound =
    Object.values(errors).includes('calc_error_no_locations_found') && !isValid;

  const departureOutsideOfSupportedRegions =
    Object.values(errors).includes('calc_error_location_is_not_supported') &&
    !isValid;

  const showDestinationIsNotSupported =
    Object.values(errors).includes('calc_error_delivery_is_not_supported') &&
    !isValid;

  const showMustSelectFromDropdown =
    Object.values(errors).includes('calc_error_select_from_search') && !isValid;

  const showFailedToFindInfoForSelectedLocation =
    Object.values(errors).includes('calc_error_failed_to_find_info') &&
    !isValid;

  const showCurrentLocationOutsideDomesticRegionError =
    Object.values(errors).includes(
      'calc_error_domestic_user_location_outside_domestic_region'
    ) && !isValid;

  const showFieldRequiredError =
    Object.values(errors).includes('fields_required') && !isValid;

  const showDimensionsError =
    (errors.size_x || errors.size_y || errors.size_z || errors.weight) &&
    !showFieldRequiredError &&
    !showCurrentLocationOutsideDomesticRegionError &&
    !isValid &&
    deliveryLimitations;

  let element: React.ReactNode = null;

  // TODO: can this can be simplified?
  if (predictionsNotFound) {
    element = (
      <InputMessage
        noMargin
        type='error'>
        {t('calc_error_no_locations_found')}
      </InputMessage>
    );
  } else if (showDestinationIsNotSupported) {
    element = (
      <InputMessage
        noMargin
        type='error'>
        {t('calc_error_delivery_is_not_supported')}
      </InputMessage>
    );
  } else if (showMustSelectFromDropdown) {
    element = (
      <InputMessage
        noMargin
        type='error'>
        {t('calc_error_select_from_search')}
      </InputMessage>
    );
  } else if (departureOutsideOfSupportedRegions) {
    element = (
      <InputMessage
        noMargin
        type='error'>
        {t('calc_error_location_is_not_supported')}
      </InputMessage>
    );
  } else if (showFailedToFindInfoForSelectedLocation) {
    element = (
      <InputMessage
        noMargin
        type='error'>
        {t('calc_error_failed_to_find_info')}
      </InputMessage>
    );
  } else if (showCurrentLocationOutsideDomesticRegionError) {
    element = (
      <InputMessage
        noMargin
        type='error'>
        {t('calc_error_domestic_user_location_outside_domestic_region')}
      </InputMessage>
    );
  } else if (showFieldRequiredError) {
    element = (
      <InputMessage
        noMargin
        type='error'>
        {t('fields_required')}
      </InputMessage>
    );
  } else if (showDimensionsError) {
    element = (
      <InputMessage
        noMargin
        type='error'>
        {t(`widget_calculator_dimensions_validations_error`, {
          minWeight: `${deliveryLimitations?.minWeight} ${t(measureUnits.weight)}`,
          maxWeight: `${deliveryLimitations?.maxWeight} ${t(measureUnits.weight)}`,
          maxSide: `${deliveryLimitations?.maxSide} ${t(measureUnits.length)}`,
        })}
      </InputMessage>
    );
  } else if (!isValid) {
    const errorMessage = Object.values(errors).filter(boolean)?.at(0);
    element = (
      <InputMessage
        noMargin
        type='error'>
        {errorMessage}
      </InputMessage>
    );
  }

  return element;
};
export default observer(ValidationHandler);
